import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Building2, Pencil, Plus, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { PermissionButton } from '@/client/components/admin/PermissionButton';
import { PERMISSIONS } from '@/client/config/permissions';
import { useGate } from '@/client/hooks/useGate';
import {
  useDeleteDepartmentMutation,
  useHrDepartmentsQuery,
  useHrEmployeesQuery,
  useUpsertDepartmentMutation,
} from '@/client/api/hr/people';
import { hasLeft, type HrDepartment } from '@/client/types/hr';
import { DeleteConfirmDialog, EmptyState, RowMenu } from '../../HumanResource/components/hr-ui';
import { BusinessPageShell } from '../components/BusinessPageShell';
import { BusinessTable, Td } from '../components/BusinessTable';

const inputClass =
  'h-9 w-full rounded-md border border-gray-200 bg-white px-3 text-sm outline-none ring-blue-500 focus:ring-2';

type Form = { name: string; code: string; parentId: string; headEmployeeId: string; costCenter: string };
const EMPTY: Form = { name: '', code: '', parentId: '', headEmployeeId: '', costCenter: '' };

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block space-y-1.5">
      <span className="text-xs font-medium text-gray-600">{label}</span>
      {children}
    </label>
  );
}

export default function DepartmentsPage() {
  const deptsQ = useHrDepartmentsQuery();
  const employees = useHrEmployeesQuery().data ?? [];
  const upsert = useUpsertDepartmentMutation();
  const remove = useDeleteDepartmentMutation();
  const canManage = useGate(PERMISSIONS.hr.manage).allowed;
  const [editing, setEditing] = useState<HrDepartment | null>(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<Form>(EMPTY);
  const [deleting, setDeleting] = useState<HrDepartment | null>(null);

  const departments = deptsQ.data ?? [];
  const byId = useMemo(() => new Map(departments.map((d) => [d.id, d])), [departments]);
  const employeeName = (id?: string | null) => employees.find((e) => e.id === id)?.name;
  const unassigned = employees.filter((e) => !hasLeft(e) && !e.departmentId).length;

  const openForm = (d: HrDepartment | null) => {
    setEditing(d);
    setForm(
      d
        ? { name: d.name, code: d.code, parentId: d.parentId ?? '', headEmployeeId: d.headEmployeeId ?? '', costCenter: d.costCenter ?? '' }
        : EMPTY,
    );
    setOpen(true);
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error('Name is required');
      return;
    }
    upsert.mutate(
      {
        id: editing?.id,
        name: form.name.trim(),
        code: form.code.trim(),
        parentId: form.parentId || null,
        headEmployeeId: form.headEmployeeId || null,
        costCenter: form.costCenter.trim() || null,
      },
      {
        onSuccess: () => {
          toast.success(editing ? 'Department updated' : 'Department added');
          setOpen(false);
        },
        onError: (err) => toast.error((err as Error).message || 'Could not save the department'),
      },
    );
  };

  const confirmDelete = () => {
    if (!deleting) return;
    const target = deleting;
    remove.mutate(target.id, {
      onSuccess: () => toast.success(`${target.name} deleted`),
      onError: (err) => toast.error((err as Error).message || 'Could not delete the department'),
    });
    setDeleting(null);
  };

  return (
    <BusinessPageShell
      title="Departments"
      description="Org units employees roll up to. Budgets and spend are reported by department."
      crumbs={[{ label: 'People', href: '/admin/human-resource' }, { label: 'Departments' }]}
      icon={Building2}
      mock={false}
      actions={
        <PermissionButton code={PERMISSIONS.hr.manage} className="gap-2" onClick={() => openForm(null)}>
          <Plus className="h-4 w-4" /> Add department
        </PermissionButton>
      }
      stats={[
        { label: 'Departments', value: String(departments.length) },
        { label: 'People placed', value: String(departments.reduce((n, d) => n + d.headcount, 0)) },
        { label: 'Not in a department', value: String(unassigned), hint: unassigned ? 'Set it from the Directory' : undefined },
      ]}
    >
      {deptsQ.isError ? (
        <EmptyState label={`Could not load departments: ${(deptsQ.error as Error)?.message ?? 'unknown error'}`} />
      ) : deptsQ.isLoading ? (
        <EmptyState label="Loading departments…" />
      ) : departments.length === 0 ? (
        <EmptyState label="No departments yet. Add the first one." />
      ) : (
        <BusinessTable headers={['Name', 'Code', 'Parent', 'Head', 'Cost center', 'Headcount', '']}>
          {departments.map((d) => (
            <tr
              key={d.id}
              className={canManage ? 'cursor-pointer hover:bg-gray-50' : 'hover:bg-gray-50'}
              onClick={() => canManage && openForm(d)}
            >
              <Td className="font-medium text-gray-900">{d.name}</Td>
              <Td>{d.code || '—'}</Td>
              <Td>{(d.parentId && byId.get(d.parentId)?.name) || '—'}</Td>
              <Td>
                {d.headEmployeeId && employeeName(d.headEmployeeId) ? (
                  <Link
                    to={`/admin/human-resource/people/directory/${d.headEmployeeId}`}
                    onClick={(e) => e.stopPropagation()}
                    className="text-slate-700 hover:underline"
                  >
                    {employeeName(d.headEmployeeId)}
                  </Link>
                ) : (
                  '—'
                )}
              </Td>
              <Td>{d.costCenter || '—'}</Td>
              <Td>{d.headcount}</Td>
              <Td className="text-right">
                <RowMenu
                  label={`Actions for ${d.name}`}
                  items={[
                    { label: 'Edit', icon: Pencil, onSelect: () => openForm(d), hidden: !canManage },
                    { label: 'Delete', icon: Trash2, onSelect: () => setDeleting(d), danger: true, hidden: !canManage },
                  ]}
                />
              </Td>
            </tr>
          ))}
        </BusinessTable>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit department' : 'Add department'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Name">
                <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className={inputClass} />
              </Field>
              <Field label="Code">
                <input
                  value={form.code}
                  onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))}
                  className={inputClass}
                  placeholder="ENG"
                />
              </Field>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Parent department">
                <select value={form.parentId} onChange={(e) => setForm((f) => ({ ...f, parentId: e.target.value }))} className={inputClass}>
                  <option value="">None</option>
                  {departments
                    .filter((d) => d.id !== editing?.id)
                    .map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.name}
                      </option>
                    ))}
                </select>
              </Field>
              <Field label="Head">
                <select
                  value={form.headEmployeeId}
                  onChange={(e) => setForm((f) => ({ ...f, headEmployeeId: e.target.value }))}
                  className={inputClass}
                >
                  <option value="">None</option>
                  {employees
                    .filter((e) => !hasLeft(e))
                    .map((e) => (
                      <option key={e.id} value={e.id}>
                        {e.name}
                      </option>
                    ))}
                </select>
              </Field>
            </div>
            <Field label="Cost center">
              <input
                value={form.costCenter}
                onChange={(e) => setForm((f) => ({ ...f, costCenter: e.target.value }))}
                className={inputClass}
                placeholder="CC-100"
              />
            </Field>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={upsert.isPending}>
                {upsert.isPending ? 'Saving…' : editing ? 'Save changes' : 'Add department'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <DeleteConfirmDialog
        open={!!deleting}
        onOpenChange={(v) => !v && setDeleting(null)}
        title="Delete department?"
        description={`"${deleting?.name}" will be deleted. Departments under it move up a level. A department that still has people cannot be deleted; move them first.`}
        onConfirm={confirmDelete}
      />
    </BusinessPageShell>
  );
}
